import React, { useState, useRef } from 'react';
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import { PiIdentificationCardFill } from 'react-icons/pi';
import { FaSquarePhone } from 'react-icons/fa6';
import Modal from "react-bootstrap/Modal";
import ReCaptcha from "react-google-recaptcha-enterprise";

import back from "~/assets/images/arrow.svg";
import valegasicon from '~/assets/images/vale-gas.svg';
import { InfoButton, InfoButtonDisabled } from '~/components/layout/Buttons';
import HeaderDesktop from "~/components/layout/Header";
import SideBar from "~/components/layout/Sidebar";
import api from '~/services/api';
import HeaderMobile from "~/components/layout/HeaderOld";
import OpenChatBot from "~/components/layout/Chatbot";
import { isValidCPF } from '~/utils/cpf';
import { isValidBrazilianMobile, formatPhone } from '~/utils/phone';
import {
  getValeGasConsumerFieldsNotice,
  isValeGasConsumerCpfPhoneRequired,
} from '~/config/valegas';

import {
  BackIcon,
  Content,
  DivisionButton,
  DivisionChangeName,
  HeaderContainer,
  HeaderMobileContainer,
  IconLoading,
  Left,
  PageContainer,
  PageTitle,
  ValeIcon,
  ModalErrorMessage,
  FormField,
  StyledInput,
  FieldError,
  RecaptchaWrapper,
  MarketNotice,
} from './styles';

function stripCpfDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function stripPhoneDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function areConsumerFieldsOkForSubmit(required, cpfRaw, phoneRaw) {
  const c = stripCpfDigits(cpfRaw);
  const p = stripPhoneDigits(phoneRaw);
  if (required) {
    if (c.length !== 11 || !isValidCPF(cpfRaw)) return false;
    if (!isValidBrazilianMobile(phoneRaw)) return false;
    return true;
  }
  if ((c.length > 0 && c.length !== 11) || (p.length > 0 && p.length !== 11)) return false;
  if (c.length === 11 && !isValidCPF(cpfRaw)) return false;
  if (p.length === 11 && !isValidBrazilianMobile(phoneRaw)) return false;
  return true;
}

export default function GasVoucherValidation() {
  const captchaSitekey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const captchaRef = useRef(null);

  const history = useHistory();
  const [codeChanged, setCodeChanged] = useState('');
  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [loadingSave, setLoadingSave] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenReCaptcha, setTokenReCaptcha] = useState(null);

  const stores = useSelector((state) => state.economicGroupReducer.stores);

  const market = localStorage.getItem('market');
  const consumerFieldsRequired = isValeGasConsumerCpfPhoneRequired(market);
  const consumerFieldsNotice = getValeGasConsumerFieldsNotice(market);

  async function handleCodeValidation() {
    setLoadingSave(true);

    const token = captchaRef.current.getValue();
    setTokenReCaptcha(token);

    const hasRequiredBase = codeChanged && token;

    if (!hasRequiredBase) {
      toast.error('Código e captcha são obrigatórios');
      setLoadingSave(false);
      return;
    }

    const c = stripCpfDigits(cpf);
    const p = stripPhoneDigits(phone);

    if (consumerFieldsRequired) {
      if (c.length !== 11 || !isValidCPF(cpf)) {
        toast.error('CPF inválido');
        setLoadingSave(false);
        return;
      }
      if (!isValidBrazilianMobile(phone)) {
        toast.error('Celular inválido');
        setLoadingSave(false);
        return;
      }
    } else if (!areConsumerFieldsOkForSubmit(false, cpf, phone)) {
      if ((c.length > 0 && c.length !== 11) || (c.length === 11 && !isValidCPF(cpf))) {
        toast.error('CPF inválido ou incompleto');
      } else if ((p.length > 0 && p.length !== 11) || (p.length === 11 && !isValidBrazilianMobile(phone))) {
        toast.error('Celular inválido ou incompleto');
      } else {
        toast.error('Verifique CPF e celular');
      }
      setLoadingSave(false);
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      access_token: localStorage.getItem('access_token'),
    };

    const url = `/residential/store/v1/voucher/user-me/validate`;
    const storeId = localStorage.getItem("storeId");

    const body = {
      voucherCode: codeChanged,
      storeId,
      captchaToken: token,
    };

    if (c.length === 11 && isValidCPF(cpf)) {
      body.consumerDocument = c;
    }
    if (isValidBrazilianMobile(phone)) {
      body.consumerPhoneNumber = p;
    }

    api.post(url, body, { headers })
      .then(() => {
        setCodeChanged('');
        setCpf('');
        setPhone('');
        toast.success("Vale validado com sucesso");
      })
      .catch((err) => {
        if (err.response?.status < 500) {
          setShowModal(true);
          setErrorMessage(err.response.data.message);
        } else {
          toast.error("Ocorreu um erro ao tentar validar o vale");
        }

        setTokenReCaptcha(null);
        captchaRef.current.reset();
      })
      .finally(() => {
        setLoadingSave(false);
      });
  }

  const handleRecaptchaChange = (value) => {
    setTokenReCaptcha(value);
  };

  const formatCPF = (value) => {
    const digits = String(value).replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return digits.replace(/(\d{3})(\d+)/, '$1.$2');
    if (digits.length <= 9) return digits.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const handleCpfChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
    if (cpfError) setCpfError('');
  };

  const handleCpfBlur = () => {
    const digits = stripCpfDigits(cpf);
    if (!digits) {
      setCpfError('');
      return;
    }
    if (digits.length !== 11) {
      setCpfError('CPF deve ter 11 dígitos');
      return;
    }
    if (!isValidCPF(cpf)) {
      setCpfError('CPF inválido');
    } else {
      setCpfError('');
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
    if (phoneError) setPhoneError('');
  };

  const handlePhoneBlur = () => {
    const digits = stripPhoneDigits(phone);
    if (!digits) {
      setPhoneError('');
      return;
    }
    if (digits.length !== 11) {
      setPhoneError('Celular deve ter 11 dígitos (com DDD)');
      return;
    }
    if (!isValidBrazilianMobile(phone)) {
      setPhoneError('Celular inválido');
    } else {
      setPhoneError('');
    }
  };

  const canValidate = Boolean(
    tokenReCaptcha &&
      codeChanged &&
      (consumerFieldsRequired ? areConsumerFieldsOkForSubmit(true, cpf, phone) : true),
  );

  return (
    <PageContainer>
      <SideBar menuSelected={"Minha Conta"}>
        <DivisionChangeName>
          <HeaderContainer>
            <HeaderDesktop stores={stores} />
          </HeaderContainer>

          <HeaderMobileContainer>
            <HeaderMobile />
          </HeaderMobileContainer>

          <Modal show={showModal} centered>
            <ModalErrorMessage>
              <WarningIcon style={{ width: '3rem', height: '3rem', marginBottom: '1rem' }} />
              <span>{errorMessage}</span>
              <InfoButton
                onClick={() => {
                  setCodeChanged('');
                  setShowModal(false);
                }}
                style={{ width: '25%', marginTop: '1rem' }}
              >
                Ok
              </InfoButton>
            </ModalErrorMessage>
          </Modal>

          <Content>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Left>
                <ValeIcon src={valegasicon} alt="Ícone do vale gás" />
                <PageTitle>Vale Gás</PageTitle>
              </Left>

              <BackIcon>
                <img
                  src={back}
                  alt="Voltar"
                  onClick={() => history.push('/valegas')}
                />
              </BackIcon>
            </div>

            {consumerFieldsNotice && (
              <MarketNotice>
                {consumerFieldsNotice.lead}
                <strong>{consumerFieldsNotice.date}</strong>
                {consumerFieldsNotice.tail}
              </MarketNotice>
            )}

            <FormField $afterNotice={!!consumerFieldsNotice}>
              <label>
                <CheckCircleIcon style={{ marginRight: '0.3rem' }} />
                Validar Vale gás
              </label>
              <StyledInput
                maxLength={99}
                value={codeChanged}
                onChange={(e) => setCodeChanged(e.target.value)}
                placeholder="Código de autorização ou n° do vale"
              />
            </FormField>

            <FormField>
              <label>
                <PiIdentificationCardFill style={{ marginRight: '0.3rem' }} />
                CPF{consumerFieldsRequired ? '' : ' (Opcional)'}
              </label>
              <StyledInput
                maxLength={14}
                inputMode="numeric"
                autoComplete="off"
                value={cpf}
                onChange={handleCpfChange}
                onBlur={handleCpfBlur}
                placeholder="N° do CPF do cliente"
              />
              {cpfError && <FieldError>{cpfError}</FieldError>}
            </FormField>

            <FormField>
              <label>
                <FaSquarePhone style={{ marginRight: '0.3rem' }} />
                Celular{consumerFieldsRequired ? '' : ' (Opcional)'}
              </label>
              <StyledInput
                maxLength={15}
                inputMode="tel"
                autoComplete="off"
                value={phone}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                placeholder="N° de celular do cliente"
              />
              {phoneError && <FieldError>{phoneError}</FieldError>}
            </FormField>

            <RecaptchaWrapper>
              <ReCaptcha
                sitekey={captchaSitekey}
                ref={captchaRef}
                onChange={handleRecaptchaChange}
              />
            </RecaptchaWrapper>

            <DivisionButton>
              {!canValidate ? (
                <InfoButtonDisabled>
                  {loadingSave ? (
                    <IconLoading className="fa fa-spinner fa-pulse fa-fw" />
                  ) : "Validar"}
                </InfoButtonDisabled>
              ) : (
                <InfoButton onClick={handleCodeValidation}>
                  {loadingSave ? (
                    <IconLoading className="fa fa-spinner fa-pulse fa-fw" />
                  ) : "Validar"}
                </InfoButton>
              )}
            </DivisionButton>

          </Content>
        </DivisionChangeName>

        <OpenChatBot />
      </SideBar>
    </PageContainer>
  );
}
